import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import type { Config, DriveStep, Driver } from 'driver.js';
import {
    GuestFreeUsageService,
    type GuestFreeUsageService as GuestFreeUsageServiceType,
} from '../../common/services/guest-free-usage-service';
import {
    GuestFreeFirstRunPromptService,
    type GuestFreeFirstRunPromptService as GuestFreeFirstRunPromptServiceType,
} from '../../common/services/guest-free-first-run-prompt-service';
import {
    OnboardingActionGuideService,
    type OnboardingActionGuideService as OnboardingActionGuideServiceType,
} from '../../common/services/onboarding-action-guide-service';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    OnboardingInputExampleGuidePresenter,
    type OnboardingInputExampleGuidePresenter as OnboardingInputExampleGuidePresenterType,
} from '../../common/services/onboarding-input-example-guide-presenter';
import {
    hasSeenOnboarding as hasStoredOnboardingState,
    markOnboardingSeen,
} from '../../common/utils/onboarding-storage';
import { TRACK_EVENT_TYPE } from '@/constants';
import {
    NoveltySearchOnboardingRuntime,
    type NoveltySearchOnboardingRuntime as NoveltySearchOnboardingRuntimeType,
} from '../novelty-search-onboarding-runtime';
import type {
    NoveltySearchOnboardingService,
    NoveltySearchOnboardingStartOptions,
} from '../novelty-search-onboarding-service';

/** 输入区首次引导本地存储 key 的版本化前缀。 */
const ONBOARDING_STORAGE_PREFIX = 'novelty-search-onboarding-v1';
/** 游客免费首跑提示本地存储 key 的版本化前缀。 */
const FREE_FIRST_RUN_STORAGE_PREFIX = 'novelty-search-free-first-run-v1';
/** 输入框与全部示例共同组成的整体高亮区域。 */
const ONBOARDING_GROUP_SELECTOR = '[data-testid="novelty-search-onboarding-group"]';
/** 查新输入区的稳定选择器。 */
const ONBOARDING_INPUT_SELECTOR = '[data-testid="novelty-search-onboarding-input"]';
/** 查新首页实际纵向滚动容器的稳定选择器。 */
const ONBOARDING_SCROLL_CONTAINER_SELECTOR = '[data-testid="novelty-search-onboarding-scroll-container"]';
/** 示例问题区域的稳定选择器。 */
const ONBOARDING_EXAMPLE_SELECTOR = '[data-testid="novelty-search-onboarding-example"]';
/** 查新提交按钮的稳定选择器。 */
const ONBOARDING_SUBMIT_SELECTOR = '[data-testid="novelty-search-onboarding-submit"]';
/** 等待异步示例区域完成渲染的最长时间。 */
const EXAMPLE_WAIT_TIMEOUT_MS = 5000;
/** 点击示例后展示提交按钮引导的延迟时间。 */
const EXAMPLE_SUBMIT_GUIDE_DELAY_MS = 2000;
/** 用户停止手动输入后展示提交按钮引导的防抖时间。 */
const MANUAL_INPUT_SUBMIT_GUIDE_DELAY_MS = 3000;
/** 免费使用资格检查和埋点统一使用的查新 Agent 类型。 */
const NOVELTY_SEARCH_TASK_TYPE = 'AI_NOVELTY_SEARCH';

/** 启动首次引导前解析得到的用户和展示状态。 */
interface OnboardingStartContext {
    /** 当前浏览器用户的稳定身份 ID。 */
    identityId: string;
    /** 当前用户是否已经展示过输入区首次引导。 */
    hasSeenOnboarding: boolean;
    /** 当前游客是否需要先展示免费首跑提示。 */
    shouldShowFreeFirstRun: boolean;
}

/** Driver 激活前解析得到的整体高亮目标和滚动容器。 */
interface OnboardingTargets {
    /** 输入框和全部示例共同组成的高亮容器。 */
    groupElement: Element;
    /** 用于挂载打字演示的输入区元素。 */
    inputElement: Element;
    /** 用于挂载手势演示的首个示例元素。 */
    exampleElement: Element;
    /** 首段引导激活期间需要锁定的首页纵向滚动容器。 */
    scrollContainerElement: Element;
}

/** 提交按钮引导激活前必须存在的页面目标。 */
interface SubmitGuideTargets {
    /** 需要挂载公共点击手势的提交按钮。 */
    submitElement: Element;
}

/**
 * 查新首页首次引导编排器。
 *
 * 负责串联免费首跑提示、输入区与示例整体高亮、提交按钮二段引导，
 * 并管理一次性状态、异步启动竞态和全部视觉资源的生命周期。
 */
@injectable()
export class NoveltySearchOnboardingServiceImpl implements NoveltySearchOnboardingService {
    /** 当前输入区首次引导使用的 Driver.js 实例。 */
    private _driver?: Driver;
    /** 输入框打字和示例双击单次演示的清理句柄。 */
    private _inputExampleGuideDisposable?: Disposable;
    /** 当前首页纵向滚动锁的清理句柄。 */
    private _scrollLockDisposable?: Disposable;
    /** 提交按钮循环点击手型的清理句柄。 */
    private _submitGuideDisposable?: Disposable;
    /** 等待展示提交按钮引导的定时器。 */
    private _submitGuideTimerId?: number;
    /** 首次 Driver 引导结束后，是否允许响应输入并触发提交引导。 */
    private _isSubmitGuideArmed = false;
    /** 提交按钮操作引导当前是否处于可归因状态。 */
    private _isSubmitGuideActive = false;
    /** 当前 service 生命周期内是否已经展示过提交按钮引导。 */
    private _hasShownSubmitGuide = false;
    /** 用于使销毁前的异步启动流程失效。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 封装 Driver、DOM、身份、存储和定时器等浏览器能力。 */
        @inject(NoveltySearchOnboardingRuntime)
        private readonly _runtime: NoveltySearchOnboardingRuntimeType,
        /** 用于判断当前用户是否具备一次免费查新资格。 */
        @inject(GuestFreeUsageService)
        private readonly _guestFreeUsageService: GuestFreeUsageServiceType,
        /** 负责展示和销毁游客免费首跑提示。 */
        @inject(GuestFreeFirstRunPromptService)
        private readonly _guestFreeFirstRunPromptService: GuestFreeFirstRunPromptServiceType,
        /** 负责动态挂载提交按钮的循环点击手型。 */
        @inject(OnboardingActionGuideService)
        private readonly _onboardingActionGuideService: OnboardingActionGuideServiceType,
        /** 统一限制全部 onboarding 仅对海外个人用户生效。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 负责挂载不修改真实输入值的输入框与示例单次演示。 */
        @inject(OnboardingInputExampleGuidePresenter)
        private readonly _inputExampleGuidePresenter: OnboardingInputExampleGuidePresenterType,
    ) {}

    /**
     * 尝试启动查新首页首次引导。
     * @param options 页面输入值和自动运行状态的惰性读取函数。
     * @returns 是否成功激活了 Driver 首次引导。
     */
    async start(options: NoveltySearchOnboardingStartOptions): Promise<boolean> {
        if (!this._onboardingEligibilityService.isEligible()) return false;

        /** 本次异步启动对应的生命周期编号。 */
        const lifecycleId = ++this._lifecycleId;
        try {
            return await this._start(options, lifecycleId);
        } catch (error) {
            this._runtime.warn('Failed to start novelty search onboarding:', error);
            return false;
        }
    }

    /** 销毁当前生命周期内的弹窗、Driver、定时器和辅助高亮。 */
    destroy(): void {
        this._lifecycleId += 1;
        this._isSubmitGuideArmed = false;
        this._clearSubmitGuide();
        this._guestFreeFirstRunPromptService.destroy();
        this._dismissActiveDriver();
    }

    /** 通知 service 用户选择了示例问题，并按示例规则准备提交按钮引导。 */
    notifyExampleSelected(): void {
        this._dismissActiveDriverForSubmitGuide();
        this._scheduleSubmitGuide(EXAMPLE_SUBMIT_GUIDE_DELAY_MS);
    }

    /**
     * 通知 service 用户产生了原生输入。
     * @param value 当前输入框文本；空文本会取消待展示的提交引导。
     */
    notifyManualInput(value: string): void {
        if (!value.trim()) {
            this._clearSubmitGuide();
            return;
        }

        this._dismissActiveDriverForSubmitGuide();
        this._scheduleSubmitGuide(MANUAL_INPUT_SUBMIT_GUIDE_DELAY_MS);
    }

    /**
     * 消费一次由提交按钮引导产生的点击归因。
     * @returns 当前点击是否发生在提交按钮引导激活期间。
     */
    consumeSubmitGuide(): boolean {
        if (!this._isSubmitGuideActive) return false;

        this._isSubmitGuideArmed = false;
        this._clearSubmitGuide();
        return true;
    }

    /**
     * 解析启动上下文并按顺序执行免费首跑提示与 Driver 引导。
     * @param options 页面当前状态读取函数。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _start(
        options: NoveltySearchOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<boolean> {
        if (this._driver?.isActive()) return false;

        /** 当前用户及两个首次展示阶段的状态。 */
        const context = await this._resolveStartContext(options, lifecycleId);
        if (!context) return false;
        if (!await this._canContinueAfterFreeFirstRun(options, context, lifecycleId)) return false;
        if (context.hasSeenOnboarding) return false;

        /** Driver 主步骤激活前必须就绪的示例辅助高亮元素和滚动容器。 */
        const targets = await this._resolveTargets(lifecycleId);
        if (!targets) return false;

        return this._activateDriver(context.identityId, targets, lifecycleId);
    }

    /**
     * 如有需要先展示游客免费首跑提示，并判断关闭后页面是否仍适合继续引导。
     * @param options 页面当前状态读取函数。
     * @param context 当前用户和首次展示状态。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _canContinueAfterFreeFirstRun(
        options: NoveltySearchOnboardingStartOptions,
        context: OnboardingStartContext,
        lifecycleId: number,
    ): Promise<boolean> {
        if (!context.shouldShowFreeFirstRun) return true;
        return this._showFreeFirstRun(options, context.identityId, lifecycleId);
    }

    /**
     * 解析身份、历史展示状态和免费使用资格。
     * @param options 页面当前状态读取函数。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _resolveStartContext(
        options: NoveltySearchOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<OnboardingStartContext | undefined> {
        /** 用于隔离首次展示状态的浏览器用户身份。 */
        const identityId = await this._resolveIdentityId(options, lifecycleId);
        if (!identityId) return;

        /** 输入区 Driver 首次引导是否已经展示。 */
        const hasSeenOnboarding = hasStoredOnboardingState(
            this._runtime,
            this._getStorageKey(ONBOARDING_STORAGE_PREFIX, identityId),
        );
        /** 游客免费首跑提示是否已经展示。 */
        const hasSeenFreeFirstRun = hasStoredOnboardingState(
            this._runtime,
            this._getStorageKey(FREE_FIRST_RUN_STORAGE_PREFIX, identityId),
        );
        /** 本次启动是否需要先展示游客免费首跑提示。 */
        const shouldShowFreeFirstRun = await this._shouldShowFreeFirstRun(
            hasSeenFreeFirstRun,
            lifecycleId,
        );
        if (!this._isCurrent(lifecycleId)) return;
        if (hasSeenOnboarding && !shouldShowFreeFirstRun) return;
        if (!await this._canUseOnce(lifecycleId)) return;

        return {
            identityId,
            hasSeenOnboarding,
            shouldShowFreeFirstRun,
        };
    }

    /**
     * 展示游客免费首跑提示，并在关闭动画结束后继续当前引导流程。
     * @param options 页面当前状态读取函数。
     * @param identityId 当前浏览器用户身份。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _showFreeFirstRun(
        options: NoveltySearchOnboardingStartOptions,
        identityId: string,
        lifecycleId: number,
    ): Promise<boolean> {
        /** 弹窗关闭动画结束后返回关闭原因的异步结果。 */
        const promptPromise = this._guestFreeFirstRunPromptService.show({
            eventType: TRACK_EVENT_TYPE.NoveltySearch,
        });
        markOnboardingSeen(
            this._runtime,
            this._getStorageKey(FREE_FIRST_RUN_STORAGE_PREFIX, identityId),
        );
        /** 免费首跑提示最终的关闭结果。 */
        const promptResult = await promptPromise;
        if (!this._isCurrent(lifecycleId) || promptResult.reason === 'destroyed') return false;

        return this._shouldShowOnCurrentPage(options);
    }

    /**
     * 检查当前用户是否仍具备一次免费查新资格。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _canUseOnce(lifecycleId: number): Promise<boolean> {
        /** 后端返回的统一 AI_NOVELTY_SEARCH 免费使用资格。 */
        const canUseOnce = await this._guestFreeUsageService.canUseOnce(NOVELTY_SEARCH_TASK_TYPE);
        return this._isCurrent(lifecycleId) && canUseOnce;
    }

    /**
     * 判断是否需要展示仅面向游客的免费首跑提示。
     * @param hasSeenFreeFirstRun 当前身份是否已展示该提示。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _shouldShowFreeFirstRun(
        hasSeenFreeFirstRun: boolean,
        lifecycleId: number,
    ): Promise<boolean> {
        if (hasSeenFreeFirstRun) return false;

        /** 当前用户是否已经登录。 */
        const isLogin = await this._runtime.isUserLogin();
        return this._isCurrent(lifecycleId) && !isLogin;
    }

    /**
     * 等待整体高亮区域、示例区域和滚动容器渲染完成，避免激活残缺引导。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _resolveTargets(lifecycleId: number): Promise<OnboardingTargets | undefined> {
        await this._runtime.waitForRender();
        const baseTargets = this._resolveBaseTargets(lifecycleId);
        if (!baseTargets) return;

        /** 异步加载完成后的首个示例区域。 */
        const exampleElement = await this._runtime.waitForElement(
            ONBOARDING_EXAMPLE_SELECTOR,
            EXAMPLE_WAIT_TIMEOUT_MS,
        );
        const currentBaseTargets = this._resolveBaseTargets(lifecycleId);
        if (!currentBaseTargets || !exampleElement) return;

        return { ...currentBaseTargets, exampleElement };
    }

    /** 读取当前生命周期内必须存在的整体区域、输入区和纵向滚动容器。 */
    private _resolveBaseTargets(lifecycleId: number): Omit<OnboardingTargets, 'exampleElement'> | undefined {
        if (!this._isCurrent(lifecycleId)) return;
        const groupElement = this._runtime.querySelector(ONBOARDING_GROUP_SELECTOR);
        const inputElement = this._runtime.querySelector(ONBOARDING_INPUT_SELECTOR);
        const scrollContainerElement = this._runtime.querySelector(ONBOARDING_SCROLL_CONTAINER_SELECTOR);
        if (!groupElement || !inputElement || !scrollContainerElement) return;
        return { groupElement, inputElement, scrollContainerElement };
    }

    /**
     * 创建并激活输入区 Driver 引导，成功后记录首次展示状态。
     * @param identityId 当前浏览器用户身份。
     * @param targets 整体高亮、单次演示和纵向滚动锁所需页面目标。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private _activateDriver(identityId: string, targets: OnboardingTargets, lifecycleId: number): boolean {
        try {
            /** 本次首次引导使用的 Driver.js 实例。 */
            const driverInstance = this._runtime.createDriver(this._createDriverConfig(lifecycleId));
            this._driver = driverInstance;
            // Driver.js 1.8 的 drive() 会为无 popover 的 step 自动补默认弹层；
            // 纯高亮场景必须走 highlight()，从 DOM 创建阶段彻底跳过 popover。
            driverInstance.highlight(this._createSteps()[0]);
            if (!driverInstance.isActive()) {
                this._dismissActiveDriver();
                return false;
            }
            this._scrollLockDisposable = this._runtime.lockVerticalScroll(targets.scrollContainerElement);
        } catch (error) {
            this._dismissActiveDriver();
            this._runtime.warn('Failed to activate novelty search input guide:', error);
            return false;
        }
        this._mountInputExampleGuide(targets);

        markOnboardingSeen(
            this._runtime,
            this._getStorageKey(ONBOARDING_STORAGE_PREFIX, identityId),
        );
        return true;
    }

    /**
     * 校验页面状态并获取用于隔离首次展示记录的用户身份。
     * @param options 页面当前状态读取函数。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _resolveIdentityId(
        options: NoveltySearchOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<string | undefined> {
        if (!this._isCurrent(lifecycleId) || !this._shouldShowOnCurrentPage(options)) return;

        /** Runtime 解析的登录用户或游客稳定身份。 */
        const identityId = await this._runtime.getIdentityId();
        if (!this._isCurrent(lifecycleId) || !identityId) return;

        return identityId;
    }

    /**
     * 判断当前页面状态是否允许展示首次引导。
     * @param options 页面当前状态读取函数。
     */
    private _shouldShowOnCurrentPage(options: NoveltySearchOnboardingStartOptions): boolean {
        return !options.getInputValue().trim()
            && !options.getIsAutoRunning();
    }

    /** 判断异步回调是否仍属于当前 service 生命周期。 */
    private _isCurrent(lifecycleId: number): boolean {
        return this._lifecycleId === lifecycleId;
    }

    /**
     * 生成按功能阶段和用户身份隔离的本地存储 key。
     * @param prefix 带版本号的阶段前缀。
     * @param identityId 当前浏览器用户身份。
     */
    private _getStorageKey(prefix: string, identityId: string): string {
        return `${prefix}:${encodeURIComponent(identityId)}`;
    }

    /**
     * 创建输入区首次引导的 Driver.js 配置。
     * @param lifecycleId Driver 创建时对应的生命周期编号。
     */
    private _createDriverConfig(lifecycleId: number): Config {
        return {
            animate: true,
            duration: 300,
            overlayColor: '#0B2447',
            overlayOpacity: 0.56,
            smoothScroll: false,
            allowClose: true,
            allowScroll: false,
            overlayClickBehavior: () => undefined,
            stagePadding: 6,
            stageRadius: 10,
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver === destroyedDriver) {
                    this._driver = undefined;
                    this._disposeInputExampleGuide();
                    this._releaseScrollLock();
                }
                if (this._isCurrent(lifecycleId)) {
                    this._isSubmitGuideArmed = true;
                }
            },
            steps: this._createSteps(),
        };
    }

    /** 创建聚焦输入框与示例整体区域的单步 Driver 引导配置。 */
    private _createSteps(): DriveStep[] {
        return [
            {
                element: ONBOARDING_GROUP_SELECTOR,
                disableActiveInteraction: false,
            },
        ];
    }

    /** 尝试挂载输入框打字和示例双击单次演示，失败时保留整体高亮。 */
    private _mountInputExampleGuide(targets: OnboardingTargets): void {
        try {
            this._inputExampleGuideDisposable = this._inputExampleGuidePresenter.show({
                exampleElement: targets.exampleElement,
                groupElement: targets.groupElement,
                inputElement: targets.inputElement,
                mode: 'typing-and-gesture',
            });
        } catch (error) {
            this._runtime.warn('Failed to mount novelty search input example guide:', error);
        }
    }

    /** 幂等卸载输入框与示例单次演示。 */
    private _disposeInputExampleGuide(): void {
        this._inputExampleGuideDisposable?.dispose();
        this._inputExampleGuideDisposable = undefined;
    }

    /** 立即关闭首段 Driver，并同步清理辅助高亮和纵向滚动锁。 */
    private _dismissActiveDriver(): void {
        const driverInstance = this._driver;
        this._driver = undefined;
        this._disposeInputExampleGuide();
        this._releaseScrollLock();
        driverInstance?.destroy();
    }

    /** 解除首页纵向滚动锁，并允许用户继续浏览页面。 */
    private _releaseScrollLock(): void {
        this._scrollLockDisposable?.dispose();
        this._scrollLockDisposable = undefined;
    }

    /** 用户开始输入或选择示例时关闭首次引导，并启用提交按钮二段引导。 */
    private _dismissActiveDriverForSubmitGuide(): void {
        if (!this._driver?.isActive()) return;

        this._isSubmitGuideArmed = true;
        this._dismissActiveDriver();
    }

    /**
     * 按交互类型延迟展示一次提交按钮引导。
     * @param delayMs 示例选择或手动输入对应的等待时间。
     */
    private _scheduleSubmitGuide(delayMs: number): void {
        if (!this._isSubmitGuideArmed || this._hasShownSubmitGuide) return;

        this._clearSubmitGuide();
        /** 安排定时器时的 service 生命周期编号。 */
        const lifecycleId = this._lifecycleId;
        this._submitGuideTimerId = this._runtime.setTimeout(() => {
            this._submitGuideTimerId = undefined;
            if (!this._isCurrent(lifecycleId) || !this._isSubmitGuideArmed) return;
            this._showSubmitGuide();
        }, delayMs);
    }

    /** 为可用的提交按钮挂载循环点击手型，不创建会阻断页面交互的遮罩。 */
    private _showSubmitGuide(): void {
        const targets = this._resolveSubmitGuideTargets();
        if (!targets) return;

        try {
            this._submitGuideDisposable = this._onboardingActionGuideService.show([
                targets.submitElement,
            ]);
        } catch (error) {
            this._clearSubmitGuide();
            this._runtime.warn('Failed to show novelty search submit guide:', error);
            return;
        }
        this._isSubmitGuideActive = true;
        this._hasShownSubmitGuide = true;
    }

    /** 获取提交按钮手势所需的页面目标。 */
    private _resolveSubmitGuideTargets(): SubmitGuideTargets | undefined {
        const submitElement = this._runtime.querySelector(ONBOARDING_SUBMIT_SELECTOR);
        if (!submitElement) return;
        if (!this._runtime.isElementEnabled(submitElement)) return;

        return { submitElement };
    }

    /** 清理提交引导的等待定时器和视觉资源。 */
    private _clearSubmitGuide(): void {
        if (this._submitGuideTimerId !== undefined) {
            this._runtime.clearTimeout(this._submitGuideTimerId);
            this._submitGuideTimerId = undefined;
        }
        this._submitGuideDisposable?.dispose();
        this._submitGuideDisposable = undefined;
        this._isSubmitGuideActive = false;
    }

}
